import { ColumnDef } from "@tanstack/react-table";
import React, { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { AvatarPlaceholder } from "../../../components/avatar/AvatarPlaceholder";
import { Badge } from "../../../components/badge/Badge";
import { GroupButtons } from "../../../components/button/GroupButtons";
import { SettingsIconButton } from "../../../components/button/SettingsIconButton";
import { SettingsCard } from "../../../components/card/SettingsCard";
import { SettingsDropdown } from "../../../components/dropdown/SettingsDropdown";
import { LottieLoading } from "../../../components/graphics/LottieLoading";
import { SettingsConfirmModal } from "../../../components/modal/SettingsConfirmModal";
import { DeactivateSvg } from "../../../components/svg/DeactivateSvg";
import { DotsSvg } from "../../../components/svg/DotsSvg";
import { EditPencilSvg } from "../../../components/svg/EditPencilSvg";
import { TrashSvg } from "../../../components/svg/TrashSvg";
import { WarningExclaimSvg } from "../../../components/svg/WarningExclaimSvg";
import { Table } from "../../../components/table/Table";
import { getInitials } from "../../../constants/constants";
import { useAppSelector } from "../../../redux/redux";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import {
  useGetTwoStepTeamQuery,
  useResetTwoStepMutation,
} from "../../../slices/TwoStepSlice";
import {
  useArchiveUserMutation,
  useGetArchivedUsersQuery,
  useGetUsersQuery,
  useUnarchiveUserMutation,
} from "../../../slices/UserSlice";
import { IUser } from "../../../types/types";
import DeleteUserModal from "../../settings-old/users/DeleteUserModal";
import { methodLabel, twoStepErrorMessage } from "../../twoStep/twoStep.utils";
import { ShieldIcon } from "../../twoStep/TwoStepUi";
import { EditUserModal } from "./EditUserModal";
import { TeamLayout } from "./TeamLayout";
import { InviteButton } from "./invites/InviteButton";
import { TeamTabs } from "./TeamTabs";

type ActiveTab = "active" | "archived";

const archiveUserTitle = (user: IUser) => {
  const name = `${user.userProfile?.firstName || ""} ${
    user.userProfile?.lastName || ""
  }`.trim();

  return name ? `Archive '${name}'?` : "Archive this user?";
};

export const Users = () => {
  const orgId = getLinkedFirm()?.orgId;

  const [activeTab, setActiveTab] = useState<ActiveTab>("active");
  const [selectedEditUser, setSelectedEditUser] = useState<IUser | null>(null);
  const [selectedDeleteUser, setSelectedDeleteUser] = useState<IUser | null>(
    null,
  );
  const [selectedArchiveUser, setSelectedArchiveUser] = useState<IUser | null>(
    null,
  );
  // Two-step sign-in (Roadmap 1845): status column + Admin "Reset" action.
  const myUserId = useAppSelector((state) => state.appReducer.user?.id);
  const { data: twoStepTeam } = useGetTwoStepTeamQuery(
    { orgId: orgId || "" },
    { skip: !orgId },
  );
  const [resetTwoStep, { isLoading: isResettingTwoStep }] =
    useResetTwoStepMutation();
  const [selectedResetUser, setSelectedResetUser] = useState<IUser | null>(
    null,
  );
  const twoStepOf = useCallback(
    (userId: string) =>
      twoStepTeam?.members.find((member) => member.userId === userId),
    [twoStepTeam],
  );

  const { data: activeUsers, isLoading: isLoadingActiveUsers } =
    useGetUsersQuery(orgId, {
      skip: !orgId || activeTab !== "active",
    });

  const { data: archivedUsers, isLoading: isLoadingArchivedUsers } =
    useGetArchivedUsersQuery(orgId, {
      skip: !orgId || activeTab !== "archived",
    });

  const [archiveUser, { isLoading: isLoadingArchive }] =
    useArchiveUserMutation();
  const [activateUser, { isLoading: isLoadingActivate }] =
    useUnarchiveUserMutation();

  const users = activeTab === "active" ? activeUsers : archivedUsers;
  const isLoading =
    activeTab === "active" ? isLoadingActiveUsers : isLoadingArchivedUsers;

  const handleEditUser = (user: IUser) => {
    setSelectedEditUser(user);
  };

  const handleArchiveUser = useCallback(
    async (user: IUser) => {
      try {
        await archiveUser(user);
        setSelectedArchiveUser(null);
        toast("User successfully archived", {
          type: "success",
        });
      } catch {
        toast("Something went wrong", {
          type: "error",
        });
      }
    },
    [archiveUser],
  );

  const handleActivateUser = useCallback(
    async (user: IUser) => {
      try {
        await activateUser(user);
        toast("User successfully activated", {
          type: "success",
        });
      } catch {
        toast("Something went wrong", {
          type: "error",
        });
      }
    },
    [activateUser],
  );

  const handleDeleteUser = (user: IUser) => {
    setSelectedDeleteUser(user);
  };

  const handleResetTwoStep = async (user: IUser) => {
    const result = await resetTwoStep({ orgId: orgId || "", userId: user.id });
    if ("error" in result) {
      toast(twoStepErrorMessage(result.error), { type: "error" });
      return;
    }
    setSelectedResetUser(null);
    toast("Two-step sign-in was reset.", { type: "success" });
  };

  const canResetTwoStep = useCallback(
    (user: IUser) =>
      !!twoStepTeam?.canReset &&
      user.id !== myUserId &&
      !!twoStepOf(user.id)?.enabled,
    [myUserId, twoStepOf, twoStepTeam?.canReset],
  );

  const userColumns = useMemo<ColumnDef<IUser>[]>(
    () => [
      {
        id: "name",
        header: "Name",
        accessorFn: (row) =>
          `${row.userProfile?.firstName || ""} ${
            row.userProfile?.lastName || ""
          }`.trim(),
        sortingFn: "alphanumeric",
        cell: ({ row }) => (
          <div className={"flex max-w-[240px] items-center gap-2"}>
            <AvatarPlaceholder
              size={"2xs"}
              type={"custom"}
              avatarCustomColor={row.original.userProfile?.color}
              label={getInitials(
                row.original.userProfile?.firstName || "",
                row.original.userProfile?.lastName || "",
              )}
            />
            <div className={"truncate font-semibold text-[#1D2C3B]"}>
              {row.original.userProfile?.firstName}{" "}
              {row.original.userProfile?.lastName}
            </div>
          </div>
        ),
        meta: { className: "py-2" },
      },
      {
        accessorKey: "userProfile.email",
        header: "Email",
        sortingFn: "alphanumeric",
        meta: {
          className: "text-[#1D2C3B] font-semibold py-2",
        },
      },
      {
        id: "connectedEmail",
        header: "Connected Email",
        accessorFn: (row) => row.nylasToken?.email ?? "",
        sortingFn: "alphanumeric",
        cell: ({ getValue }) => {
          const value = getValue<string>();
          return <span>{value?.trim() || "—"}</span>;
        },
        meta: {
          className: "text-[#1D2C3B] font-semibold py-2",
        },
      },
      {
        id: "role",
        header: "Role",
        accessorFn: (row) => row.userRoleEnum?.userRole ?? "",
        cell: ({ row }) => (
          <Badge
            label={row.original.userRoleEnum?.userRole || "Unknown"}
            variant={"status-grey"}
          />
        ),
        meta: { className: "py-2" },
      },
      ...(twoStepTeam?.available
        ? [
            {
              id: "twoStep",
              header: "Two-step",
              accessorFn: (row: IUser) =>
                twoStepOf(row.id)?.enabled ? "On" : "Off",
              cell: ({ row }: { row: { original: IUser } }) => {
                const status = twoStepOf(row.original.id);
                return status?.enabled ? (
                  <span
                    title={methodLabel(status.method)}
                    className={
                      "inline-flex items-center gap-1 rounded-full bg-[#E9F9F1] px-2 py-0.5 text-xs font-bold text-[#147A4B]"
                    }>
                    <ShieldIcon size={12} />
                    On
                  </span>
                ) : (
                  <span
                    className={
                      "inline-flex rounded-full bg-[#F1F2F5] px-2 py-0.5 text-xs font-bold text-[#6B7185]"
                    }>
                    Off
                  </span>
                );
              },
              meta: { className: "py-2" },
            } as ColumnDef<IUser>,
          ]
        : []),
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <div className={"flex justify-end"}>
            <SettingsDropdown
              actionsList={
                activeTab === "active"
                  ? [
                      {
                        name: "Edit",
                        action: () => void handleEditUser(row.original),
                        icon: <EditPencilSvg />,
                      },
                      {
                        name: "Archive",
                        action: () => setSelectedArchiveUser(row.original),
                        icon: <DeactivateSvg />,
                        disabled: isLoadingArchive,
                      },
                      ...(canResetTwoStep(row.original)
                        ? [
                            {
                              name: "Reset two-step sign-in",
                              action: () => setSelectedResetUser(row.original),
                              icon: <ShieldIcon size={16} />,
                            },
                          ]
                        : []),
                    ]
                  : [
                      {
                        name: "Edit",
                        action: () => void handleEditUser(row.original),
                        icon: <EditPencilSvg />,
                      },
                      {
                        name: "Activate",
                        action: () => {
                          void handleActivateUser(row.original);
                        },
                        icon: <DeactivateSvg />,
                        disabled: isLoadingActivate,
                      },
                      {
                        name: "Delete",
                        action: () => handleDeleteUser(row.original),
                        icon: <TrashSvg />,
                        extraClasses: "text-[#DC2626]",
                        iconColor: "text-[#DC2626]",
                      },
                    ]
              }>
              <SettingsIconButton
                icon={<DotsSvg />}
                onClick={() => null}
                size={"small"}
              />
            </SettingsDropdown>
          </div>
        ),
        meta: { className: "py-2" },
      },
    ],
    [
      activeTab,
      canResetTwoStep,
      handleActivateUser,
      isLoadingActivate,
      isLoadingArchive,
      twoStepOf,
      twoStepTeam?.available,
    ],
  );

  return (
    <>
      <TeamLayout>
        <SettingsCard extraClasses={"p-4"}>
          <div className={"mb-4 flex justify-between gap-4"}>
            <TeamTabs activeTab={"users"} />

            <div className={"flex gap-4"}>
              <GroupButtons
                buttons={[
                  {
                    label: "Active",
                    action: () => setActiveTab("active"),
                    isActive: activeTab === "active",
                  },
                  {
                    label: "Archived",
                    action: () => setActiveTab("archived"),
                    isActive: activeTab === "archived",
                  },
                ]}
              />

              <InviteButton />
            </div>
          </div>

          <div>
            {isLoading ? (
              <LottieLoading />
            ) : (
              <Table
                data={users || []}
                columns={userColumns}
                initialSorting={[{ id: "name", desc: false }]}
              />
            )}
          </div>
        </SettingsCard>
      </TeamLayout>
      {selectedEditUser && (
        <EditUserModal
          user={selectedEditUser}
          teammates={activeUsers ?? []}
          onClose={() => setSelectedEditUser(null)}
        />
      )}
      {selectedArchiveUser && (
        <SettingsConfirmModal
          onClose={() => setSelectedArchiveUser(null)}
          onConfirm={() => void handleArchiveUser(selectedArchiveUser)}
          icon={<WarningExclaimSvg sizeCx={"h-6 w-6"} />}
          title={archiveUserTitle(selectedArchiveUser)}
          description={
            "Are you sure you want to archive this user? They will no longer be able to sign in to ProCharted. You can activate them again from the Archived tab."
          }
          confirmText={"Archive"}
          variant={"warning"}
          isLoading={isLoadingArchive}
        />
      )}
      {selectedResetUser && (
        <SettingsConfirmModal
          onClose={() => setSelectedResetUser(null)}
          onConfirm={() => void handleResetTwoStep(selectedResetUser)}
          icon={<WarningExclaimSvg sizeCx={"h-6 w-6"} />}
          title={"Reset two-step sign-in?"}
          description={
            "Use this when someone lost their phone and their recovery codes. Their authenticator / email code, recovery codes and remembered computers are removed. They can sign in with just their password again, or, if your firm requires two-step sign-in, they set it up again at their next sign-in. This is recorded in the Activity Log."
          }
          confirmText={"Reset"}
          variant={"warning"}
          isLoading={isResettingTwoStep}
        />
      )}
      {selectedDeleteUser && (
        <DeleteUserModal
          user={selectedDeleteUser}
          onClose={() => {
            setSelectedDeleteUser(null);
          }}
          isActive={!!selectedDeleteUser}
        />
      )}
    </>
  );
};
